import { ColumnsType } from 'antd/es/table'
import { REPORT_TABLE_TYPES } from 'Components/UI/EAReport/constants'
import summaryReportData, {
  ReportColumnSummaryInfo,
} from 'Components/UI/EAReport/Table/Summary/utils/summaryReportData'
import moment from 'moment'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'

import { ExportColumn } from '../exportUtils'

pdfMake.vfs = pdfFonts.pdfMake.vfs

interface PdfCell {
  text: string | null | undefined
  alignment?: string
  bold?: boolean
  fillColor?: string
  fontSize?: number
  colSpan?: number
  margin?: number[]
}

interface ExportToPDFParams<T extends object> {
  columns: ColumnsType<T>
  dataSource: T[]
  filtrosDetalhados?: string[]
  reportName: string
  title: string
  exportDetails?: boolean
}

const formatCellValue = (column: ExportColumn, record: Record<string, unknown>): string | null | undefined => {
  const value = record[column.dataIndex]

  if (column.exportRender) return String(column.exportRender(value, record))
  if (column.type && REPORT_TABLE_TYPES.includes(column.type as never))
    return record[`${column.dataIndex}_str`] as string
  return value as string | null | undefined
}

interface ExtractedColumns {
  mainColumns: ExportColumn[]
  detailColumns: ExportColumn[]
  detailDataIndex: string
}

const getColumns = (cols: ExportColumn[]): ExtractedColumns => {
  const detailColumnDef = cols.find((col) => col.columns)
  const mainColumns = cols.filter((col) => (!col.columns || col.columns.length === 0) && col.exportable !== false)
  const detailColumns = detailColumnDef?.columns
    ? detailColumnDef.columns.filter((col) => col.exportable !== false)
    : []
  return { mainColumns, detailColumns, detailDataIndex: detailColumnDef?.dataIndex ?? 'detalhes' }
}

const getMetadataContent = (title: string, filtrosDetalhados?: string[]) => [
  { bold: true, text: `EasyAssist Web - ${title}` },
  ...(Array.isArray(filtrosDetalhados) ? filtrosDetalhados.map((detalhe) => ({ text: detalhe })) : []),
  { margin: [0, 0, 0, 20], text: `Gerado em: ${moment().format('DD/MM/YYYY HH:mm:ss')}` },
]

export const buildSummaryLabelRow = (columnCount: number): PdfCell[] => [
  { bold: true, colSpan: columnCount, fillColor: '#ddd', text: 'Total' },
  ...Array.from({ length: Math.max(columnCount - 1, 0) }, () => ({ text: '' })),
]

export const getMainColumnWidths = (columnCount: number): string[] => Array.from({ length: columnCount }, () => '*')

const buildSimpleTable = (
  mainColumns: ExportColumn[],
  data: Record<string, unknown>[],
  hasSummary: boolean,
  mainAsSummaryInfo: ReportColumnSummaryInfo[],
) => {
  const header: PdfCell[] = mainColumns.map((column) => ({
    alignment: (column as { align?: string }).align ?? 'left',
    bold: true,
    fillColor: '#ddd',
    text: column.title,
  }))

  const body: PdfCell[][] = data.map((record) =>
    mainColumns.map((column) => ({
      alignment: (column as { align?: string }).align ?? 'left',
      text: formatCellValue(column, record),
    })),
  )

  const tableBody = [header, ...body]
  let summaryData: PdfCell[] = []

  if (hasSummary) {
    summaryData = summaryReportData(mainAsSummaryInfo, data).map((col) => ({
      alignment: col.align,
      bold: true,
      fillColor: '#ddd',
      text: col.total_str,
    }))
  }

  return {
    table: {
      body: hasSummary ? [...tableBody, buildSummaryLabelRow(mainColumns.length), summaryData] : tableBody,
      headerRows: 1,
      widths: getMainColumnWidths(mainColumns.length),
    },
  }
}

const detailsLayout = {
  hLineWidth: () => 0.2,
  vLineWidth: () => 0.2,
  hLineColor: () => '#aaa',
  vLineColor: () => '#aaa',
}

export const buildMainRowBlock = (
  record: Record<string, unknown>,
  index: number,
  mainColumns: ExportColumn[],
  mainColumnWidths: string[],
) => {
  const headerCells: PdfCell[] = mainColumns.map((column) => ({
    alignment: (column as { align?: string }).align ?? 'left',
    bold: true,
    fillColor: '#f0f0f0',
    text: column.title,
  }))
  const valueCells: PdfCell[] = mainColumns.map((column) => ({
    alignment: (column as { align?: string }).align ?? 'left',
    text: formatCellValue(column, record),
  }))

  return {
    table: { body: [headerCells, valueCells], headerRows: 1, widths: mainColumnWidths },
    layout: detailsLayout,
    margin: [0, index === 0 ? 0 : 15, 0, 0],
  }
}

const buildDetailTableBlock = (
  details: Record<string, unknown>[],
  detailColumns: ExportColumn[],
  hasSummaryDetails: boolean,
  detailAsSummaryInfo: ReportColumnSummaryInfo[],
) => {
  const detailHeader: PdfCell[] = detailColumns.map((column) => ({
    alignment: (column as { align?: string }).align ?? 'left',
    bold: true,
    fillColor: '#ddd',
    text: column.title,
    fontSize: 8,
  }))

  const detailBody: PdfCell[][] = details.map((detailRecord) =>
    detailColumns.map((column) => ({
      alignment: (column as { align?: string }).align ?? 'left',
      text: formatCellValue(column, detailRecord),
      fontSize: 8,
    })),
  )

  const summaryDetails: PdfCell[] = summaryReportData(detailAsSummaryInfo, details).map((col) => ({
    alignment: col.align,
    bold: true,
    fillColor: '#ddd',
    text: col.total_str,
  }))

  return {
    table: {
      body: hasSummaryDetails
        ? [detailHeader, ...detailBody, buildSummaryLabelRow(detailColumns.length), summaryDetails]
        : [detailHeader, ...detailBody],
      headerRows: 1,
      widths: detailColumns.map(() => (detailColumns.length > 6 ? 'auto' : '*')),
    },
    layout: detailsLayout,
    margin: [20, 5, 0, 0],
  }
}

export const buildFinalSummaryBlock = (
  mainColumns: ExportColumn[],
  mainColumnWidths: string[],
  mainAsSummaryInfo: ReportColumnSummaryInfo[],
  data: Record<string, unknown>[],
) => {
  const summary: PdfCell[] = summaryReportData(mainAsSummaryInfo, data).map((col) => ({
    alignment: col.align,
    bold: true,
    fillColor: '#ddd',
    text: col.total_str,
  }))

  return {
    table: {
      body: [buildSummaryLabelRow(mainColumns.length), summary],
      headerRows: 0,
      widths: mainColumnWidths,
    },
    margin: [0, 20, 0, 0],
  }
}

const exportToPDF = <T extends object>({
  columns,
  dataSource,
  filtrosDetalhados,
  reportName,
  title,
  exportDetails = false,
}: ExportToPDFParams<T>): void => {
  const cols = columns as unknown as ExportColumn[]
  const data = dataSource as unknown as Record<string, unknown>[]

  const { mainColumns, detailColumns, detailDataIndex } = getColumns(cols)
  const reportTimeStamp = moment().format('DD_MM_YYYY_HH_mm_ss')
  const metadataContent = getMetadataContent(title, filtrosDetalhados)

  const mainAsSummaryInfo = mainColumns as unknown as ReportColumnSummaryInfo[]
  const hasSummary = mainColumns.some((column) => column.summary === true)

  const tableElements: unknown[] = []

  if (!exportDetails) {
    tableElements.push(buildSimpleTable(mainColumns, data, hasSummary, mainAsSummaryInfo))
  } else {
    const mainColumnWidths = getMainColumnWidths(mainColumns.length)
    const detailAsSummaryInfo = detailColumns as unknown as ReportColumnSummaryInfo[]
    const hasSummaryDetails = detailColumns.some((column) => column.summary === true)

    data.forEach((record, index) => {
      const detailData = record[detailDataIndex]
      const details = Array.isArray(detailData) ? (detailData as Record<string, unknown>[]) : []
      tableElements.push(buildMainRowBlock(record, index, mainColumns, mainColumnWidths))
      if (details.length > 0) {
        tableElements.push(buildDetailTableBlock(details, detailColumns, hasSummaryDetails, detailAsSummaryInfo))
      }
    })

    if (hasSummary) {
      tableElements.push(buildFinalSummaryBlock(mainColumns, mainColumnWidths, mainAsSummaryInfo, data))
    }
  }

  const finalContent = [...metadataContent, ...tableElements]

  // eslint-disable-next-line no-void
  void pdfMake
    .createPdf({ content: finalContent as never, defaultStyle: { fontSize: 10 }, pageOrientation: 'landscape' })
    .download(`relatorio_${reportName}_${reportTimeStamp}`)
}

export default exportToPDF
